import {
  searchExpressionMissingAliasLength,
  searchExpressionSpacedValueCanExtend,
  searchExpressionTokenIsStandaloneOperator,
  searchExpressionTokenIsValue,
  searchExpressionTokenText,
  spanForSearchExpressionToken,
} from "../../js/query/lex.js";
import { SearchExpressionBooleanParser } from "../../js/query/boolean-parser.js";
import { normalizeSearchExpressionSourceBindingSidecar } from "./source-bindings.js";

function rawValueFromToken(token) {
  return {
    value: searchExpressionTokenText(token),
    span: spanForSearchExpressionToken(token),
  };
}

function rawPrefixAllowsSpacedValue(prefix) {
  const normalized = String(prefix ?? "").trim().toLowerCase().replace(/-/g, "_");
  return [
    "fish",
    "title",
    "item",
    "npc",
    "zone",
    "rod",
    "region",
    "regions",
    "regiongroup",
    "regiongroups",
    "region_group",
    "region_groups",
  ].includes(normalized);
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function normalizeRawSpan(span) {
  const start = Number.parseInt(span?.start, 10);
  const end = Number.parseInt(span?.end, 10);
  const normalizedStart = Number.isInteger(start) && start >= 0 ? start : 0;
  return {
    start: normalizedStart,
    end: Number.isInteger(end) && end >= normalizedStart ? end : normalizedStart,
  };
}

function nodeSpan(node) {
  return spanForSearchExpressionToken(node?.span);
}

function spanFromNodes(first, last) {
  const firstSpan = nodeSpan(first);
  const lastSpan = nodeSpan(last ?? first);
  return {
    start: firstSpan.start,
    end: lastSpan.end,
  };
}

function makeRawGroup(operator, children, span = null) {
  const normalizedChildren = (Array.isArray(children) ? children : []).filter(Boolean);
  if (!normalizedChildren.length) {
    return null;
  }
  return {
    type: "group",
    operator: String(operator ?? "").trim().toLowerCase() === "and" ? "and" : "or",
    children: normalizedChildren,
    span: span || spanFromNodes(normalizedChildren[0], normalizedChildren.at(-1)),
  };
}

function withRawNegation(node, negationStart, negated) {
  if (!node || !negated) {
    return node;
  }
  return {
    ...node,
    negated: true,
    span: {
      start: negationStart ?? nodeSpan(node).start,
      end: nodeSpan(node).end,
    },
  };
}

class RawSearchExpressionDslParser extends SearchExpressionBooleanParser {
  constructor(input, options = {}) {
    super(input, options);
    this.resolvedSpacedValueEnds = options.resolvedSpacedValueEnds instanceof Map
      ? options.resolvedSpacedValueEnds
      : new Map();
    this.allowUnquotedMissingAlias = options.allowUnquotedMissingAlias !== false;
  }

  makeGroupNode(operator, children) {
    return makeRawGroup(operator, children);
  }

  supportsContainerScope() {
    return true;
  }

  makeContainerNode(expression, openToken, closeToken) {
    return {
      type: "container",
      expression,
      span: {
        start: openToken.start,
        end: closeToken.end,
      },
    };
  }

  parsePrefixAliasExpression(negated, negationStart) {
    if (!this.allowUnquotedMissingAlias) {
      return null;
    }
    const aliasLength = searchExpressionMissingAliasLength(this.tokens, this.index);
    if (!aliasLength) {
      return null;
    }
    const firstToken = this.current();
    const lastToken = this.tokens[this.index + aliasLength - 1];
    this.index += aliasLength;
    return {
      type: "term",
      term: {
        kind: "bare",
        value: this.source.slice(firstToken.start, lastToken.end),
      },
      span: {
        start: negationStart ?? firstToken.start,
        end: lastToken.end,
      },
      ...(negated ? { negated: true } : {}),
    };
  }

  applyParsedNegation(node, negationStart) {
    return withRawNegation(node, negationStart, true);
  }

  handleUnterminatedGroup(openToken) {
    this.addIssue({
      code: "unterminated_group",
      span: { start: openToken?.start ?? 0, end: this.previousEnd() },
      expected: [")"],
      message: "Expected a closing parenthesis.",
    });
  }

  handleUnterminatedContainer(openToken) {
    this.addIssue({
      code: "unterminated_container",
      span: { start: openToken?.start ?? 0, end: this.previousEnd() },
      expected: ["]"],
      message: "Expected a closing container bracket.",
    });
  }

  decorateParenthesizedNode(node, openToken, closeToken) {
    return {
      ...node,
      parenthesized: true,
      span: {
        start: openToken.start,
        end: closeToken.end,
      },
    };
  }

  parseSourceKinds() {
    if (!this.matchSymbol("@")) {
      return [];
    }
    const sourceKinds = [];
    const first = this.parseValueToken();
    if (!first) {
      return sourceKinds;
    }
    sourceKinds.push(rawValueFromToken(first));
    while (this.matchSymbol(",")) {
      const next = this.parseValueToken();
      if (!next) {
        return sourceKinds;
      }
      sourceKinds.push(rawValueFromToken(next));
    }
    return sourceKinds;
  }

  parsePossiblySpacedPrefixedValue(firstToken) {
    const resolvedValue = this.parseResolvedSpacedValue(firstToken);
    if (resolvedValue) {
      return resolvedValue;
    }
    if (!firstToken || firstToken.type !== "word") {
      return rawValueFromToken(firstToken);
    }
    let lastToken = firstToken;
    let scanIndex = this.index;
    let previousEnd = firstToken.end;
    while (scanIndex < this.tokens.length) {
      const token = this.tokens[scanIndex];
      const gap = this.source.slice(previousEnd, token.start);
      if (
        /[\n()]/u.test(gap) ||
        !searchExpressionTokenIsValue(token) ||
        searchExpressionTokenIsStandaloneOperator(token)
      ) {
        break;
      }
      if (
        token.type === "word" &&
        this.tokens[scanIndex + 1]?.type === "symbol" &&
        this.tokens[scanIndex + 1].value === ":"
      ) {
        break;
      }
      lastToken = token;
      scanIndex += 1;
      previousEnd = token.end;
    }
    this.index = scanIndex;
    return {
      value: this.source.slice(firstToken.start, lastToken.end),
      span: {
        start: firstToken.start,
        end: lastToken.end,
      },
    };
  }

  parseResolvedSpacedValue(firstToken) {
    if (!firstToken || !this.resolvedSpacedValueEnds.has(firstToken.start)) {
      return null;
    }
    const end = this.resolvedSpacedValueEnds.get(firstToken.start);
    if (!Number.isInteger(end) || end < firstToken.end || end > this.source.length) {
      return null;
    }
    let scanIndex = this.index;
    let lastEnd = firstToken.end;
    let lastToken = firstToken;
    while (scanIndex < this.tokens.length && this.tokens[scanIndex].end <= end) {
      const token = this.tokens[scanIndex];
      if (!searchExpressionSpacedValueCanExtend({
        source: this.source,
        firstToken,
        previousToken: lastToken,
        previousEnd: lastEnd,
        token,
        nextToken: this.tokens[scanIndex + 1],
      })) {
        return null;
      }
      lastEnd = token.end;
      lastToken = token;
      scanIndex += 1;
    }
    if (lastEnd !== end) {
      return null;
    }
    this.index = scanIndex;
    return {
      value: end === firstToken.end
        ? searchExpressionTokenText(firstToken)
        : this.source.slice(firstToken.start, end),
      span: { start: firstToken.start, end },
    };
  }

  parseLayerFactConditions() {
    if (!this.matchSymbol("[")) {
      return [];
    }
    const conditions = [];
    if (this.matchSymbol("]")) {
      return conditions;
    }
    while (this.current()) {
      const firstToken = this.current();
      const start = firstToken?.start ?? this.previousEnd();
      let end = start;
      while (this.current()) {
        const token = this.current();
        if (token.type === "symbol" && (token.value === "," || token.value === "]")) {
          break;
        }
        end = token.end;
        this.index += 1;
      }
      const value = this.source.slice(start, end).trim();
      if (value) {
        conditions.push({ value, span: { start, end } });
      }
      if (this.matchSymbol("]")) {
        return conditions;
      }
      if (!this.matchSymbol(",")) {
        return conditions;
      }
    }
    return conditions;
  }

  parseBracketBody() {
    const openToken = this.current();
    if (!this.matchSymbol("[")) {
      return null;
    }
    const contentStart = openToken.end;
    let depth = 1;
    while (this.current()) {
      const token = this.current();
      if (token.type === "symbol" && token.value === "[") {
        depth += 1;
      } else if (token.type === "symbol" && token.value === "]") {
        depth -= 1;
        if (depth === 0) {
          const contentEnd = token.start;
          this.index += 1;
          return {
            value: this.source.slice(contentStart, contentEnd),
            span: { start: contentStart, end: contentEnd },
            end: token.end,
          };
        }
      }
      this.index += 1;
    }
    return null;
  }

  parsePrefixedTerm(prefix, prefixToken) {
    if (!this.matchSymbol(":")) {
      return null;
    }

    if (prefix === "items") {
      const values = this.parseValueListTokens();
      if (!values) {
        return null;
      }
      const sourceKinds = this.parseSourceKinds();
      const lastSpan = sourceKinds.at(-1)?.span || spanForSearchExpressionToken(
        values.at(-1),
        this.previousEnd(),
      );
      return {
        type: "term",
        term: {
          kind: "prefixed-list",
          prefix,
          values: values.map(rawValueFromToken),
          ...(sourceKinds.length ? { sourceKinds } : {}),
        },
        span: {
          start: prefixToken.start,
          end: lastSpan.end,
        },
      };
    }

    const valueToken = this.parseValueToken();
    if (!valueToken) {
      return null;
    }

    if (prefix === "semantic") {
      if (!this.matchSymbol(":")) {
        return null;
      }
      const fieldToken = this.parseValueToken();
      if (!fieldToken) {
        return null;
      }
      return {
        type: "term",
        term: {
          kind: "semantic",
          layer: rawValueFromToken(valueToken),
          field: rawValueFromToken(fieldToken),
        },
        span: {
          start: prefixToken.start,
          end: fieldToken.end,
        },
      };
    }

    if (prefix === "filter") {
      const predicate = this.parseBracketBody();
      if (!predicate) {
        return null;
      }
      return {
        type: "term",
        term: {
          kind: "layer-filter",
          layer: rawValueFromToken(valueToken),
          predicate: {
            value: predicate.value,
            span: predicate.span,
          },
        },
        span: {
          start: prefixToken.start,
          end: predicate.end,
        },
      };
    }

    if (prefix === "fact") {
      if (!this.matchSymbol(":")) {
        return null;
      }
      const factToken = this.parseValueToken();
      if (!factToken) {
        return null;
      }
      const conditions = this.parseLayerFactConditions();
      const end = Math.max(factToken.end, this.previousEnd());
      return {
        type: "term",
        term: {
          kind: "layer-fact",
          layer: rawValueFromToken(valueToken),
          fact: rawValueFromToken(factToken),
          ...(conditions.length ? { conditions } : {}),
        },
        span: {
          start: prefixToken.start,
          end,
        },
      };
    }

    const value = rawPrefixAllowsSpacedValue(prefix)
      ? this.parsePossiblySpacedPrefixedValue(valueToken)
      : rawValueFromToken(valueToken);
    const sourceKinds = prefix === "item" || prefix === "fish" ? this.parseSourceKinds() : [];
    const end = sourceKinds.at(-1)?.span?.end ?? value.span.end;
    return {
      type: "term",
      term: {
        kind: "prefixed",
        prefix,
        value: value.value,
        valueSpan: value.span,
        ...(sourceKinds.length ? { sourceKinds } : {}),
      },
      span: {
        start: prefixToken.start,
        end,
      },
    };
  }

  parseTerm() {
    const token = this.parseValueToken();
    if (!token) {
      return null;
    }
    const value = searchExpressionTokenText(token).trim();
    const prefix = value.toLowerCase();
    if (this.current()?.type === "symbol" && this.current().value === ":") {
      return this.parsePrefixedTerm(prefix, token);
    }
    const resolvedValue = this.parseResolvedSpacedValue(token) || rawValueFromToken(token);
    return {
      type: "term",
      term: {
        kind: "bare",
        value: resolvedValue.value,
      },
      span: resolvedValue.span,
    };
  }
}

function parseRawSearchExpressionDslResultWithOptions(input, options) {
  const source = String(input ?? "");
  if (!source.trim()) {
    return { expression: null, issues: [] };
  }
  const parser = new RawSearchExpressionDslParser(source, options);
  const expression = parser.parse();
  return {
    expression,
    issues: parser.issues.slice(),
  };
}

export function parseRawSearchExpressionDslResult(input) {
  return parseRawSearchExpressionDslResultWithOptions(input, {});
}

export function parseResolvedRawSearchExpressionDslResult(
  input,
  options,
) {
  return parseRawSearchExpressionDslResultWithOptions(input, options || {});
}

function normalizeRawValueRef(value) {
  if (!isPlainObject(value)) {
    return null;
  }
  const rawValue = String(value.value ?? "").trim();
  if (!rawValue) {
    return null;
  }
  return {
    value: rawValue,
    span: normalizeRawSpan(value.span),
  };
}

function normalizeRawValueRefs(values) {
  return Array.isArray(values)
    ? values.map(normalizeRawValueRef).filter(Boolean)
    : [];
}

function normalizeRawTermPayload(term) {
  if (!isPlainObject(term)) {
    return null;
  }
  const kind = String(term.kind ?? "").trim();
  if (kind === "bare") {
    const value = String(term.value ?? "").trim();
    return value ? { kind, value } : null;
  }
  if (kind === "prefixed") {
    const prefix = String(term.prefix ?? "").trim().toLowerCase();
    const value = String(term.value ?? "").trim();
    if (!prefix || !value) {
      return null;
    }
    const normalized = {
      kind,
      prefix,
      value,
      valueSpan: normalizeRawSpan(term.valueSpan),
    };
    const sourceKinds = normalizeRawValueRefs(term.sourceKinds);
    if (sourceKinds.length) {
      normalized.sourceKinds = sourceKinds;
    }
    return normalized;
  }
  if (kind === "prefixed-list") {
    const prefix = String(term.prefix ?? "").trim().toLowerCase();
    if (!prefix) {
      return null;
    }
    const normalized = {
      kind,
      prefix,
      values: normalizeRawValueRefs(term.values),
    };
    const sourceKinds = normalizeRawValueRefs(term.sourceKinds);
    if (sourceKinds.length) {
      normalized.sourceKinds = sourceKinds;
    }
    return normalized;
  }
  if (kind === "semantic") {
    const layer = normalizeRawValueRef(term.layer);
    const field = normalizeRawValueRef(term.field);
    return layer && field ? { kind, layer, field } : null;
  }
  if (kind === "layer-fact") {
    const layer = normalizeRawValueRef(term.layer);
    const fact = normalizeRawValueRef(term.fact);
    const conditions = normalizeRawValueRefs(term.conditions);
    return layer && fact
      ? { kind, layer, fact, ...(conditions.length ? { conditions } : {}) }
      : null;
  }
  if (kind === "layer-filter") {
    const layer = normalizeRawValueRef(term.layer);
    const predicate = normalizeRawValueRef(term.predicate);
    return layer && predicate ? { kind, layer, predicate } : null;
  }
  return null;
}

function preserveRootResolvedBindings(normalized, node, isRoot) {
  if (!isRoot || !Object.hasOwn(node, "resolvedBindings")) {
    return normalized;
  }
  const resolvedBindings = normalizeSearchExpressionSourceBindingSidecar(
    node.resolvedBindings,
  );
  if (resolvedBindings) {
    normalized.resolvedBindings = resolvedBindings;
  }
  return normalized;
}

function normalizeRawSearchExpressionNode(node, isRoot) {
  if (!isPlainObject(node)) {
    return null;
  }
  if (node.type === "term") {
    const term = normalizeRawTermPayload(node.term);
    if (!term) {
      return null;
    }
    const normalized = {
      type: "term",
      term,
      span: normalizeRawSpan(node.span),
    };
    if (node.negated === true) {
      normalized.negated = true;
    }
    if (node.parenthesized === true) {
      normalized.parenthesized = true;
    }
    return preserveRootResolvedBindings(normalized, node, isRoot);
  }
  if (node.type === "group") {
    const children = Array.isArray(node.children)
      ? node.children.map((child) => normalizeRawSearchExpressionNode(child, false)).filter(Boolean)
      : [];
    if (!children.length) {
      return null;
    }
    const normalized = {
      type: "group",
      operator: String(node.operator ?? "").trim().toLowerCase() === "and" ? "and" : "or",
      children,
      span: normalizeRawSpan(node.span),
    };
    if (node.negated === true) {
      normalized.negated = true;
    }
    if (node.parenthesized === true) {
      normalized.parenthesized = true;
    }
    return preserveRootResolvedBindings(normalized, node, isRoot);
  }
  if (node.type === "container") {
    const expression = normalizeRawSearchExpressionNode(node.expression, false);
    if (!expression) {
      return null;
    }
    const normalized = {
      type: "container",
      expression,
      span: normalizeRawSpan(node.span),
    };
    if (node.negated === true) {
      normalized.negated = true;
    }
    if (node.parenthesized === true) {
      normalized.parenthesized = true;
    }
    return preserveRootResolvedBindings(normalized, node, isRoot);
  }
  return null;
}

export function normalizeRawSearchExpressionTree(node) {
  return normalizeRawSearchExpressionNode(node, true);
}
